import { useCallback, useEffect, useState } from "react";
import {
  useParams,
  useNavigate,
  Link,
  useSearchParams,
} from "react-router-dom";
import Swal from "sweetalert2";
import {
  apiRequest,
  formatCurrency,
  formatDate,
  getProfileImage,
} from "../../../core/common/api";
import {
  creditTypeLabel,
  creditStatusLabel,
  canManageCredit,
  toneForCreditStatus,
  toneForCreditType,
} from "../../../shared/Utils";
import { canManageUsers } from "../../../core/common/adminPermissions";
import ToggleSwitch from "../../../shared/ToggleSwitch";
import AppImage from "../../../shared/AppImage";
import MetricCard from "../../../shared/MetricCard";

type UserFormState = {
  name: string;
  email: string;
  phone: string;
  isActive: boolean;
};

const orderStatusTone = (status = "") => {
  if (["Delivered", "Refunded", "Return Approved"].includes(status))
    return "success";
  if (["Processing", "Shipped", "Return Requested"].includes(status))
    return "warning";
  if (["Cancelled", "Return Rejected"].includes(status)) return "danger";
  return "muted";
};

const registerFromLabel = (value = "") => {
  if (value === "web") return "Website";
  if (value === "app") return "Mobile App";
  return value || "—";
};

const shortId = (id = "") =>
  id ? `#${String(id).slice(-8).toUpperCase()}` : "—";

const UserDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [user, setUser] = useState<any>(null);
  const [stats, setStats] = useState<any>({});
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [credit, setCredit] = useState<any>({ history: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<UserFormState>({
    name: "",
    email: "",
    phone: "",
    isActive: true,
  });

  const canEditUser = canManageUsers();

  const fetchUser = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const res = await apiRequest(`/users/${id}`, "GET");
      if (res.success && res.user) {
        setUser(res.user);
        setForm({
          name: res.user.name || "",
          email: res.user.email || "",
          phone: res.user.phone || "",
          isActive: res.user.isActive !== false,
        });
        setStats(res.stats || {});
        setRecentOrders(res.recentOrders || []);
        setAddresses(res.addresses || []);
        setCredit(res.credit || { history: [] });
        setIsEditing(false);
      } else {
        throw new Error(res.message || "User not found");
      }
    } catch (err: any) {
      Swal.fire("Error", err.message || "Failed to load user", "error");
      navigate("/users");
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    if (searchParams.get("edit") === "1" && canEditUser && user) {
      setIsEditing(true);
    }
  }, [searchParams, canEditUser, user]);

  const handleCancelEdit = () => {
    if (user) {
      setForm({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        isActive: user.isActive !== false,
      });
    }
    setIsEditing(false);
  };

  const handleSaveUser = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user || !canEditUser) return;

    setIsSaving(true);
    try {
      const res = await apiRequest(`/users/${user._id}`, "PUT", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        isActive: form.isActive,
      });

      if (!res.success) throw new Error(res.message || "Unable to update user");

      await Swal.fire({
        title: "User Updated",
        text: "Customer details saved successfully.",
        icon: "success",
        timer: 1400,
        showConfirmButton: false,
      });

      await fetchUser();
    } catch (err: any) {
      Swal.fire(
        "Update Failed",
        err.message || "Could not update user",
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!user) return;
    const confirm = await Swal.fire({
      title: `Delete ${user.name}?`,
      text: "This will permanently remove the account and all associated data.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Delete Account",
    });
    if (!confirm.isConfirmed) return;
    try {
      const res = await apiRequest(`/users/${user._id}`, "DELETE");
      if (res.success) {
        Swal.fire({
          title: "Deleted",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        navigate("/users");
      }
    } catch (err: any) {
      Swal.fire(
        "Delete Failed",
        err.message || "Could not delete user",
        "error",
      );
    }
  };

  const handleSetCreditLimit = async () => {
    if (!user) return;

    const response = await Swal.fire({
      title: "Set Credit Limit",
      html: `
        <p style="margin:0 0 10px;font-size:13px;color:#475569;text-align:left;line-height:1.45;">
          This is the <strong>maximum approved udhaar</strong>. Using credit does not reduce this number.
          Available = Limit − Due.
        </p>
        <input id="credit-limit-amount" class="swal2-input" type="number" min="0" step="0.01" value="${credit.creditLimit || 0}" placeholder="Credit limit amount" />
        <input id="credit-limit-note" class="swal2-input" placeholder="Note (optional)" />
      `,
      showCancelButton: true,
      confirmButtonText: "Save Limit",
      confirmButtonColor: "#9b6c2b",
      preConfirm: () => {
        const amount = Number(
          (document.getElementById("credit-limit-amount") as HTMLInputElement)
            ?.value || 0,
        );
        const note =
          (document.getElementById("credit-limit-note") as HTMLInputElement)
            ?.value || "";
        if (Number.isNaN(amount) || amount < 0)
          return Swal.showValidationMessage("Enter a valid credit limit");
        if (amount < Number(credit.outstandingBalance || 0)) {
          return Swal.showValidationMessage(
            "Limit cannot be lower than the outstanding balance",
          );
        }
        return { amount, note };
      },
    });

    if (!response.isConfirmed || !response.value) return;

    try {
      const result = await apiRequest(
        `/credit/users/${user._id}/limit`,
        "PUT",
        {
          creditLimit: response.value.amount,
          note: response.value.note,
        },
      );
      if (!result.success)
        throw new Error(result.message || "Unable to update credit limit");
      await Swal.fire({
        title: "Credit Limit Updated",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
      await fetchUser();
    } catch (error: any) {
      Swal.fire(
        "Update Failed",
        error.message || "Unable to update credit limit",
        "error",
      );
    }
  };

  const handleSettlement = async () => {
    if (!user || Number(credit.outstandingBalance || 0) <= 0) return;

    const outstanding = Number(credit.outstandingBalance || 0);

    const response = await Swal.fire({
      title: "Record Credit Payment",
      html: `
        <p style="margin-bottom:10px;font-size:14px;color:#475569;">Due amount: <strong>${formatCurrency(outstanding)}</strong></p>
        <p style="margin:0 0 10px;font-size:13px;color:#64748b;text-align:left;line-height:1.45;">
          Settlement reduces due and increases available credit. Credit limit stays the same.
        </p>
        <input id="credit-payment-amount" class="swal2-input" type="number" min="0.01" step="0.01" value="${outstanding}" placeholder="Amount" />
        <input id="credit-payment-reference" class="swal2-input" placeholder="Payment reference (recommended)" />
        <input id="credit-payment-note" class="swal2-input" placeholder="Note (optional)" />
      `,
      showCancelButton: true,
      showDenyButton: true,
      denyButtonText: "Settle Full Amount",
      confirmButtonText: "Settle Payment",
      confirmButtonColor: "#9b6c2b",
      didOpen: () => {
        const amountInput = document.getElementById(
          "credit-payment-amount",
        ) as HTMLInputElement;
        if (amountInput) amountInput.value = String(outstanding);
      },
      preDeny: () => {
        const amountInput = document.getElementById(
          "credit-payment-amount",
        ) as HTMLInputElement;
        if (amountInput) amountInput.value = String(outstanding);
        return false;
      },
      preConfirm: () => {
        const amount = Number(
          (document.getElementById("credit-payment-amount") as HTMLInputElement)
            ?.value || 0,
        );
        const paymentReference =
          (
            document.getElementById(
              "credit-payment-reference",
            ) as HTMLInputElement
          )?.value || "";
        const note =
          (document.getElementById("credit-payment-note") as HTMLInputElement)
            ?.value || "";
        if (amount <= 0 || amount > outstanding) {
          Swal.showValidationMessage(
            `Enter an amount up to ${formatCurrency(outstanding)}`,
          );
          return undefined;
        }
        return { amount, paymentReference, note };
      },
    });

    if (!response.isConfirmed || !response.value) return;

    try {
      const result = await apiRequest(
        `/credit/users/${user._id}/settle`,
        "POST",
        response.value,
      );
      if (!result.success)
        throw new Error(result.message || "Unable to settle credit payment");

      const settledOrders = (result.settledOrders || [])
        .map((item: any) => item.order)
        .filter(Boolean);

      await Swal.fire({
        title: result.duplicate ? "Already Recorded" : "Payment Settled",
        html: `
          <p>${result.message || "Settlement recorded."}</p>
          <p style="margin-top:10px;font-size:13px;color:#475569;line-height:1.5;">
            Limit stays <strong>${formatCurrency(result.summary?.creditLimit ?? credit.creditLimit)}</strong>.<br />
            Due is now <strong>${formatCurrency(result.summary?.outstandingBalance ?? 0)}</strong>.<br />
            Available is now <strong>${formatCurrency(result.summary?.availableCredit ?? 0)}</strong>.
          </p>
          ${
            settledOrders.length
              ? `<p style="margin-top:8px;font-size:13px;color:#475569;">Orders updated: ${settledOrders.length}</p>`
              : ""
          }
        `,
        icon: result.duplicate ? "info" : "success",
        timer: settledOrders.length ? undefined : 2800,
        showConfirmButton: settledOrders.length > 0,
      });
      await fetchUser();
    } catch (error: any) {
      Swal.fire(
        "Settlement Failed",
        error.message || "Unable to settle payment",
        "error",
      );
    }
  };

  const handleCopyUserId = async () => {
    if (!user?._id) return;
    try {
      await navigator.clipboard.writeText(String(user._id));
      await Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "User ID copied",
        showConfirmButton: false,
        timer: 1200,
      });
    } catch {
      Swal.fire("Copy Failed", "Could not copy user ID", "error");
    }
  };

  if (isLoading) {
    return (
      <div className="page-section loading-state">
        <h2>Loading User Profile…</h2>
      </div>
    );
  }

  if (!user) return null;

  const orderBreakdown: Record<string, number> = stats.orderBreakdown || {};
  const breakdownEntries = Object.entries(orderBreakdown);
  const canManageCreditAccess = canManageCredit();
  const isActiveAccount = user.isActive !== false;
  const creditLimit = Number(credit.creditLimit || 0);
  const outstandingBalance = Number(credit.outstandingBalance || 0);
  const availableCredit = Number(credit.availableCredit || 0);
  const creditHistory = credit.history || [];

  return (
    <div className="detail-page">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <Link to="/users" className="btn-sm btn-outline back-link">
            ← Back to Users
          </Link>
          <div className="page-eyebrow">Customer Profile</div>
          <h1 className="page-title">{user.name}</h1>
          <div className="page-sub">
            {user.email}
            {user.phone ? ` · ${user.phone}` : ""}
            {" · "}Joined {formatDate(user.createdAt)}
          </div>
        </div>
        <div className="page-hdr-right">
          <span
            className={`status-badge ${isActiveAccount ? "success" : "danger"}`}
          >
            {isActiveAccount ? "Active" : "Inactive"}
          </span>
          {canEditUser && !isEditing && (
            <button
              type="button"
              className="btn-sm btn-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit User
            </button>
          )}
          {canEditUser && isEditing && (
            <>
              <button
                type="button"
                className="btn-sm btn-outline"
                onClick={handleCancelEdit}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="user-edit-form"
                className="btn-sm btn-primary"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </>
          )}
          {canEditUser && (
            <button
              type="button"
              className="btn-sm btn-danger"
              onClick={handleDelete}
            >
              Delete Account
            </button>
          )}
        </div>
      </div>

      <div className="detail-grid-2">
        <div className="table-card profile-card">
          <AppImage
            src={getProfileImage(user)}
            alt={user.name}
            crossOrigin="anonymous"
            className="profile-avatar-img"
          />

          <div>
            <div className="profile-name">{user.name}</div>
            <div className="profile-email">{user.email}</div>
          </div>

          <div className="profile-status-row">
            <span
              className={`status-badge ${isActiveAccount ? "success" : "danger"}`}
            >
              {isActiveAccount ? "Active" : "Inactive"}
            </span>
            <span className="status-badge muted">
              {registerFromLabel(user.registerFrom)}
            </span>
          </div>

          <div className="profile-meta">
            <div className="profile-meta-row">
              <span>User ID</span>
              <strong className="profile-id-value">
                {shortId(user._id)}
                <button
                  type="button"
                  className="id-copy-btn"
                  onClick={handleCopyUserId}
                >
                  Copy
                </button>
              </strong>
            </div>
            <div className="profile-meta-row">
              <span>Phone</span>
              <strong>{user.phone || "—"}</strong>
            </div>
            <div className="profile-meta-row">
              <span>Last login</span>
              <strong>
                {user.lastLoginAt ? formatDate(user.lastLoginAt) : "Never"}
              </strong>
            </div>
            <div className="profile-meta-row">
              <span>Addresses</span>
              <strong>{addresses.length} saved</strong>
            </div>
            <div className="profile-meta-row">
              <span>Wishlist</span>
              <strong>{stats.wishlistCount ?? 0} items</strong>
            </div>
          </div>
        </div>

        <div className="page-stack">
          <section className="detail-metrics-row metrics-grid">
            {[
              {
                label: "Total Orders",
                value: stats.totalOrders ?? 0,
                icon: "📦",
                tone: "blue",
                helper: "All orders placed by this customer",
              },
              {
                label: "Total Spent",
                value: formatCurrency(stats.totalSpent ?? 0),
                icon: "💰",
                tone: "green",
                helper: "Excludes cancelled and refunded",
              },
              {
                label: "Wishlist Items",
                value: stats.wishlistCount ?? 0,
                icon: "❤️",
                tone: "pink",
                helper: "Saved products",
              },
            ].map((s) => (
              <MetricCard key={s.label} metric={s} />
            ))}
          </section>

          {breakdownEntries.length > 0 && (
            <div className="table-card info-card">
              <h3 className="info-card-title">Order Status Breakdown</h3>
              <div className="status-chips">
                {breakdownEntries.map(([status, count]) => (
                  <div key={status} className="status-chip">
                    <span className={`status-badge ${orderStatusTone(status)}`}>
                      {status}
                    </span>
                    <span>{count as number}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {isEditing && canEditUser && (
        <form
          id="user-edit-form"
          onSubmit={handleSaveUser}
          className="table-card"
        >
          <div className="table-head">
            <h2>Edit Customer Details</h2>
            <p>Update name, email, phone and account status</p>
          </div>
          <div className="admin-card-body admin-form-grid">
            <div className="form-field">
              <label className="modal-label">Full Name</label>
              <input
                className="admin-input"
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                required
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Email Address</label>
              <input
                type="email"
                className="admin-input"
                value={form.email}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    email: event.target.value,
                  }))
                }
                required
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Phone Number</label>
              <input
                className="admin-input"
                value={form.phone}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    phone: event.target.value,
                  }))
                }
                placeholder="Optional"
              />
            </div>
            <div className="form-field admin-status-field">
              <ToggleSwitch
                variant="row"
                label="Active account"
                hint="Customer can sign in and place orders"
                checked={form.isActive}
                onChange={(isActive) =>
                  setForm((current) => ({ ...current, isActive }))
                }
              />
            </div>
          </div>
        </form>
      )}

      <section className="table-card">
        <div className="table-head table-head-flex">
          <div>
            <h2>Credit Account</h2>
            <p>Limit − Due = Available</p>
          </div>
          <div className="hdr-actions">
            {canManageCreditAccess && (
              <>
                <button
                  type="button"
                  className="btn-sm btn-outline"
                  onClick={handleSetCreditLimit}
                >
                  Set Limit
                </button>
                <button
                  type="button"
                  className="btn-sm btn-primary"
                  onClick={handleSettlement}
                  disabled={outstandingBalance <= 0}
                >
                  Settle
                </button>
              </>
            )}
            <Link
              to={`/credit-history?userId=${user._id}`}
              className="btn-sm btn-outline"
            >
              View All
            </Link>
          </div>
        </div>

        <div className="credit-metrics-row metrics-grid">
          {[
            {
              label: "Credit Limit",
              value: formatCurrency(creditLimit),
              icon: "💳",
              tone: "blue",
            },
            {
              label: "Used / Due",
              value: formatCurrency(outstandingBalance),
              icon: "⏳",
              tone: "orange",
            },
            {
              label: "Available Credit",
              value: formatCurrency(availableCredit),
              icon: "✅",
              tone: "green",
            },
          ].map((item) => (
            <MetricCard key={item.label} metric={item} />
          ))}
        </div>

        {creditHistory.length > 0 ? (
          <div className="orders-table-scroll">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Order</th>
                  <th>Amount</th>
                  <th>Due</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {creditHistory.slice(0, 8).map((transaction: any) => (
                  <tr key={transaction._id}>
                    <td>
                      <span
                        className={`status-badge ${toneForCreditType(transaction.type)}`}
                      >
                        {creditTypeLabel(transaction.type)}
                      </span>
                    </td>
                    <td>
                      {transaction.order?._id ? (
                        <Link
                          to={`/orders/${transaction.order._id}`}
                          className="order-code"
                        >
                          {transaction.order.orderCode}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <strong>{formatCurrency(transaction.amount)}</strong>
                    </td>
                    <td>{formatCurrency(transaction.balanceAfter)}</td>
                    <td>
                      <span
                        className={`status-badge ${toneForCreditStatus(transaction.status)}`}
                      >
                        {creditStatusLabel(transaction.status)}
                      </span>
                    </td>
                    <td>{formatDate(transaction.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">No credit activity yet.</div>
        )}
      </section>

      <section className="table-card">
        <div className="table-head">
          <h2>Recent Orders ({recentOrders.length})</h2>
          <p>Last 5 orders placed by this customer</p>
        </div>

        {recentOrders.length === 0 ? (
          <div className="empty-state">No orders placed yet.</div>
        ) : (
          <div className="orders-table-scroll">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Items</th>
                  <th>Payment</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order: any) => (
                  <tr key={order._id}>
                    <td>
                      <Link to={`/orders/${order._id}`} className="order-code">
                        {order.orderCode ||
                          `#${String(order._id).slice(-8).toUpperCase()}`}
                      </Link>
                    </td>
                    <td>
                      {order.itemsCount} item{order.itemsCount !== 1 ? "s" : ""}
                    </td>
                    <td>
                      <span
                        className={`status-badge ${order.paymentStatus === "Completed" ? "success" : "warning"}`}
                      >
                        {order.paymentStatus}
                      </span>
                    </td>
                    <td>
                      <strong>{formatCurrency(order.totalPrice)}</strong>
                    </td>
                    <td>
                      <span
                        className={`status-badge ${orderStatusTone(order.orderStatus)}`}
                      >
                        {order.orderStatus}
                      </span>
                    </td>
                    <td>{formatDate(order.createdAt)}</td>
                    <td>
                      <Link
                        to={`/orders/${order._id}`}
                        className="btn-sm btn-outline"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="table-card">
        <div className="table-head">
          <h2>Saved Addresses ({addresses.length})</h2>
          <p>Delivery addresses saved by this customer</p>
        </div>
        {addresses.length === 0 ? (
          <div className="empty-state">No saved addresses yet.</div>
        ) : (
          <div className="address-grid">
            {addresses.map((addr: any, i: number) => (
              <div
                key={addr._id || i}
                className={`address-card ${addr.isDefault ? "is-default" : ""}`}
              >
                {addr.isDefault && (
                  <span className="address-default-badge">Default</span>
                )}
                <div className="address-label">{addr.label || "home"}</div>
                <div>{addr.fullName}</div>
                <div>{addr.address}</div>
                <div>
                  {addr.city}, {addr.state} — {addr.pinCode}
                </div>
                <div>{addr.country}</div>
                <div className="text-muted-sm">{addr.phoneNo}</div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default UserDetail;
